import { HttpClient, HttpEventType, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { cloneDeep, forEach } from "lodash";
import { catchError, finalize, map, takeUntil } from "rxjs/operators";
import { EMPTY, Observable, Subject, throwError } from "rxjs";
import { Store } from "@ngrx/store";
import { VngDialogService } from "@vengreso/angular-components";
import { Router } from "@angular/router";
import { detect } from "detect-browser";

import { environment } from "src/environments/environment";
import { getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { DIALOG_WIDTHS, DialogService } from "src/app/core/core-services/services/dialog.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { WarningDialogComponent } from "../../warning-dialog/warning-dialog.component";
import { ExtensionService } from "../extension.service";
import { LoadingService } from "../loader.service";
import { NotificationService } from "./../notification.service";
import { ErrorDialogComponent } from "./error-dialog/error-dialog.component";
import { PlanState } from "src/app/core/store/plan/plan.reducer";
import { loadUserCurrentSubscriptionApi } from "src/app/core/store/plan/plan.actions";
import { SignInErrorComponent } from "src/app/session/signin/sign-in-error/sign-in-error.component";

/** Shared by `getAdvancedSearchResult` and its plan-block branch in `handleError`. */
const ADVANCED_SEARCH_URL = "v1/user/advanced-search-data";

/**
 * Request options accepted by every DataService verb.
 * Success payloads follow the {@link ApiResponse} envelope — pass the
 * expected type as the verb's generic: `get<ApiResponse<FlyCut[]>>(...)`.
 */
class Info {
  /** API path relative to the environment API root (e.g. "v1/user/shortcut"). */
  url: string;
  /** JSON body (POST/PUT/PATCH) or query params (GET/DELETE). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  /**
   * JSON body for DELETE, whose `data` is query params. Use it when the
   * endpoint reads a payload rather than a filter (e.g. the deletion `reason`
   * on `v1/user`) — free text has no business in a URL or an access log.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  /** Set false to skip the global loading indicator (shown by default). */
  isLoader?: boolean;
  loaderName?: string;
  /** Set internally; used to decide when the NgRx plan store must refresh. */
  httpType?: string;
  file?: File;
  /** Set true to bypass the centralized error dialogs/toasts. */
  ignoreErrors?: boolean;
}

@Injectable()
export class DataService {
  downloadComplete$ = new Subject<string>();

  /**
   * Reference counts per loader name (QA #602 F-4). `LoadingService`/
   * `NgxUiLoaderService.start()`/`.stop()` share one default taskId across
   * every caller that omits `loaderName`, so two concurrent requests on the
   * same (often unnamed/"master") loader were really sharing one underlying
   * task: whichever request stopped first removed it, hiding the overlay
   * while the other was still in flight — a pre-existing defect QA saw on
   * both builds (S-17), sharpened by the AC-6 `finalize()` fix into a
   * regression: a write issued from another write's own success handler
   * (e.g. `postA(...).subscribe(() => postB(...))`) had A's contribution
   * zeroed out (by `extractData()`, which used to stop the loader on success
   * too — see its own comment) before B's `startLoader()` had run, hiding
   * the overlay while B was still in flight. Counting here — independent of
   * however ngx-ui-loader tracks its own task — keeps the overlay up while
   * any loader-owning request (same loader name) is in flight, and hides it
   * only once the last one settles or is cancelled.
   */
  private readonly loaderTaskCounts = new Map<string, number>();

  /**
   * post()/patch()/put()/delete() can still call `stopLoader(info)` twice for
   * one request on the error path: once from `handleError()`, once from
   * `finalize()` on every terminal path (issue #600 AC-6). Both calls must
   * balance exactly the one `startLoader(info)` call for that request, so
   * the second call for the same `info` object is a no-op — otherwise a
   * single request would double-decrement the count above and hide the
   * overlay while a true sibling request was still in flight.
   */
  private readonly loaderStopped = new WeakSet<Info>();

  constructor(
    private readonly http: HttpClient,
    public dialog: DialogService,
    private readonly loadingService: LoadingService,
    private readonly notification: NotificationService,
    private readonly sharedData: SharedDataService,
    private readonly router: Router,
    private readonly dialogRef: VngDialogService,
    private readonly extensionService: ExtensionService,
    private readonly store: Store<PlanState>
  ) {}

  private loaderKey(info: Info): string {
    return info.loaderName ?? "";
  }

  startLoader(info: Info) {
    // Start loader before API call
    if (info.isLoader === false) {
      return;
    }
    const key = this.loaderKey(info);
    const count = (this.loaderTaskCounts.get(key) ?? 0) + 1;
    this.loaderTaskCounts.set(key, count);
    if (count === 1) {
      this.loadingService.start(info.loaderName, "DataService");
    }
  }

  stopLoader(info: Info) {
    // Reset the loader
    if (info.isLoader === false) {
      return;
    }
    if (this.loaderStopped.has(info)) {
      return;
    }
    this.loaderStopped.add(info);
    const key = this.loaderKey(info);
    const count = Math.max(0, (this.loaderTaskCounts.get(key) ?? 0) - 1);
    this.loaderTaskCounts.set(key, count);
    if (count === 0) {
      this.loadingService.stop(info.loaderName, "DataService");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any>(info: Info): Observable<T> {
    this.startLoader(info);
    return this.http
      .post(this.apiURL + info.url, info.data, {
        headers: {
          browser: detect()?.name,
        },
      })
      .pipe(
        map((res: Response) => {
          info.httpType = "post";
          // QA #602 F-4: extractData() no longer stops the loader itself for
          // this success path (`false`) — see the comment on finalize() below.
          return this.extractData(false, res, info);
        }),
        catchError((err: Response) => {
          return this.handleError(err, info);
        }),
        // issue #600 AC-6: neither extractData()/handleError() nor this
        // finalize() alone covers every path — extractData()/handleError()
        // don't run when the subscription is torn down before either arrives
        // (e.g. takeUntil(_destroy$) firing on navigation away mid-request),
        // which used to strand the app-wide ngx-ui-loader overlay on.
        // finalize() runs on every path (next/error/unsubscribe), so an
        // aborted request always balances its startLoader().
        //
        // QA #602 F-4: extractData()'s own stopLoader() call for a *success*
        // response used to run here too, from inside this map() — which the
        // RxJS operator chain runs strictly *before* this subscription's own
        // `next` callback fires. A caller that starts another loader-owning
        // request from that `next` callback (`postA(...).subscribe(() =>
        // postB(...))`) would have A's loader contribution already zeroed out
        // before B's startLoader() ever ran, so DataService's reference count
        // (above) saw a false "nothing left in flight" and closed the overlay
        // early — reopened by B's own startLoader(), then closed again when B
        // settled: a visible flicker, not the continuously-up overlay AC-6
        // requires. finalize()'s teardown runs after `next` (and after
        // complete/error), by which point a chained B has already
        // incremented the count — so finalize() is this verb's one and only
        // stop signal for a normal success; stopLoader()'s own idempotency
        // guard (this.loaderStopped) still protects the error path, where
        // handleError() below stops the loader before this finalize() also
        // tries to.
        finalize(() => this.stopLoader(info))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any>(info: Info): Observable<T> {
    this.startLoader(info);
    return this.http
      .patch(this.apiURL + info.url, info.data, {
        headers: {
          browser: detect()?.name,
        },
      })
      .pipe(
        map((res: Response) => {
          info.httpType = "patch";
          // See the identical comment in post() above (QA #602 F-4).
          return this.extractData(false, res, info);
        }),
        catchError((err: Response) => {
          return this.handleError(err, info);
        }),
        // See the identical comment in post() above (issue #600 AC-6 / QA #602 F-4).
        finalize(() => this.stopLoader(info))
      );
  }

  postFormData(info: Info) {
    try {
      let browser = detect();
      let browser_type;
      if (browser && (browser?.name == "edge" || browser?.name == "edge-chromium")) {
        browser_type = "edge";
      } else {
        browser_type = browser?.name;
      }

      const url = this.apiURL + info.url;
      const jsonData = {
        ...(info.data ?? {}),
        browser: browser_type,
      };

      let body = `browser=${browser_type}`;
      Object.keys(jsonData).forEach((key) => {
        body += `&${key}=${jsonData[key]}`;
      });

      return this.http.post(url, body);
    } catch (err) {
      return;
    }
  }

  postExternal(info: Info): Observable<Response> {
    this.startLoader(info);

    return this.http.post(info.url, info.data).pipe(
      map((res: Response) => {
        return this.extractData(true, res, info);
      }),
      catchError((err: Response) => {
        this.stopLoader(info);
        return throwError(err);
      })
    );
  }

  getExternal(info: Info): Observable<Response> {
    this.startLoader(info);

    return this.http.get(info.url, { observe: "response" as "body" }).pipe(
      map((res: Response) => {
        // Refresh on Update
        return this.extractData(false, res.body, info);
      }),
      catchError((err: Response) => {
        this.stopLoader(info);
        return throwError(err);
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any>(info: Info): Observable<T> {
    this.startLoader(info);

    return this.http.put(this.apiURL + info.url, info.data).pipe(
      map((res: Response) => {
        info.httpType = "put";
        // See the identical comment in post() above (QA #602 F-4).
        return this.extractData(false, res, info);
      }),
      catchError((err: Response) => {
        return this.handleError(err, info);
      }),
      // See the identical comment in post() above (issue #600 AC-6 / QA #602 F-4).
      finalize(() => this.stopLoader(info))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(info: Info): Observable<T> {
    // this.startLoader(info);

    return this.http.get(this.apiURL + info.url, { params: info.data, observe: "response" as "body" }).pipe(
      map((res: Response) => {
        // Refresh on Update
        this.trackVersionUpdate(res.headers.get("FlyMSG-FrontEnd-Version"));
        return this.extractData(false, res.body, info);
      }),
      catchError((err: Response) => {
        if (info.url.includes("clicked_settings")) {
          return EMPTY;
        }
        return this.handleError(err, info);
      })
    );
  }

  download(info: Info, filename: string) {
    this.startLoader(info);
    this.http.get(this.apiURL + info.url, { responseType: "blob" }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url); // Clean up
        this.stopLoader(info);
        this.downloadComplete$.next(filename);
      },
      error: (error) => {
        this.stopLoader(info);
        this.handleError(error, info);
      },
    });
  }

  uploadFile(info: Info, cancelUpload: Subject<void>): Observable<number | HttpResponse<any>> {
    this.startLoader(info);
    const formData: FormData = new FormData();
    formData.append("csv_file", info.file);

    return this.http
      .post(this.apiURL + info.url, formData, {
        reportProgress: true,
        observe: "events",
      })
      .pipe(
        takeUntil(cancelUpload),
        map((event) => {
          this.stopLoader(info);
          if (event.type === HttpEventType.UploadProgress) {
            return Math.round((100 * event.loaded) / (event.total ?? 1));
          } else if (event instanceof HttpResponse) {
            return event;
          }
          return 0;
        }),
        catchError((err: Response) => {
          this.stopLoader(info);
          return this.handleError(err, info);
        })
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any>(info: Info): Observable<T> {
    this.startLoader(info);

    return this.http.delete(this.apiURL + info.url, { params: info.data, body: info.body }).pipe(
      map((res: Response) => {
        info.httpType = "delete";
        // See the identical comment in post() above (QA #602 F-4).
        return this.extractData(false, res, info);
      }),
      catchError((err: Response) => {
        return this.handleError(err, info);
      }),
      // See the identical comment in post() above (issue #600 AC-6 / QA #602 F-4).
      finalize(() => this.stopLoader(info))
    );
  }

  extractData(bool, res: any, info: Info) {
    // `bool` (true only for postExternal(), which has no finalize() backstop
    // for its success path) stops the loader here. post()/patch()/put()/
    // delete() pass false and rely solely on their finalize() operator (QA
    // #602 F-4): this map() callback runs before this subscription's own
    // `next` callback, so stopping the loader here for those verbs could
    // zero out a request's contribution to DataService's reference count
    // before a request chained from that `next` callback had a chance to add
    // its own.
    if (bool) {
      this.stopLoader(info);
    }

    this.updatePlanStore(info?.httpType, info.url);

    return res;
  }

  /**
   * The account-deactivated dialog + sign-out flow. Extracted (issue #605
   * F-3) so a caller that owns its own refusal handling via
   * `ignoreErrors: true` — `StripeService.startSalesProTrial()` — can still
   * run it: that endpoint's `ignoreErrors` only exists to stop this method's
   * *other* branches (the generic 4xx/5xx toasts) from double-firing
   * alongside the endpoint's own message, not to let a deactivated account
   * through un-stopped.
   */
  handleDeactivatedAccount(errorResponse: Response | any): void {
    setTimeout(() => {
      const dialogHandle = this.dialog.openDialogComponent(
        SignInErrorComponent,
        {
          admin_email: errorResponse?.error?.admin_email,
        },
        "500px"
      );

      dialogHandle.afterClosed().subscribe(() => {
        this.dialogRef.closeAll();
        this.extensionService.sendLogoutMessage();
        this.sharedData.clear();
        setTimeout(() => {
          this.router.navigate(["/session/signin"]);
        }, 1000);
      });
    }, 2000);
  }

  /**
   * The validation `error_list` toast/dialog split. Extracted (issue #605
   * F-4) for the same reason as `handleDeactivatedAccount()` above: a
   * caller with `ignoreErrors: true` must still show a validation 422's own
   * text, not fall through to generic fallback copy.
   */
  handleValidationErrorList(errorList: Record<string, string[]>): void {
    let errors: any[] = [];
    forEach(errorList, (array) => {
      errors = errors.concat(array);
    });
    const errorCount = errors.length;
    if (errorCount === 1) {
      this.notification.toast(errors[0], "OK", 6000);
    } else if (errorCount > 1) {
      this.dialog.openDialogComponent(ErrorDialogComponent, { data: errors }, "500px");
    }
  }

  handleError(errorResponse: Response | any, info: Info) {
    this.stopLoader(info);

    if (!info.ignoreErrors) {
      if (errorResponse?.status == 403 && errorResponse?.error?.code == "DEACTIVATED") {
        this.handleDeactivatedAccount(errorResponse);
      } else {
        if (errorResponse.status && errorResponse.status >= 500) {
          this.notification.toast("Technical Error! Please contact administrator.", "OK", 10000);

          if (environment.production) {
            // this.dialogRef.closeAll();
            // this.extensionService.sendLogoutMessage();
            // this.sharedData.clear();
            // setTimeout(() => {
            //   this.router.navigate(["/session/signin"])
            // }, 1000);
          }
          this.router.navigate(["/session/error"]);
        } else if (errorResponse.error) {
          // Advanced search is paywalled server-side and rejects with 422
          // (not the 403 every other plan limit uses), so it never reached the
          // upsell branches below — the request just failed and the results
          // page rendered empty with the reason only visible in devtools.
          // Handled before them because the local `getPlanId()` check that
          // guards the search dialog (advanced-search-dialog `canEdit()`) only
          // covers "Free"; the backend is the authority on the entitlement.
          if (info.url == ADVANCED_SEARCH_URL && (errorResponse.status == 422 || errorResponse.status == 403)) {
            const planId = getPlanId();
            this.dialog.openDialogComponent(
              WarningDialogComponent,
              { type: "advancedSearch", error: "advancedSearch", showTheStarter: planId === "Free" },
              DIALOG_WIDTHS.lg
            );
            return throwError(errorResponse);
          }

          if (errorResponse.status == 403) {
            let planId = getPlanId();

            let type;
            if (
              info.url == "v1/user/shortcut" &&
              errorResponse.error?.error ==
                "You have reached limit, the number of flycuts that can be created with current plan"
            ) {
              type = "numOfFlycuts";
              this.dialog.openDialogComponent(
                WarningDialogComponent,
                {
                  self: this,
                  type,
                  showTheStarter: planId === "Free",
                  error: planId === "Free" ? "maxFree" : "maxStarter",
                },
                "800px"
              );
              return EMPTY;
            }
            if (
              info.url == "v1/user/import/shortcut" &&
              errorResponse.error?.error ==
                "You have reached limit, the number of flycuts that can be created with current plan"
            ) {
              type = "import";
              this.dialog.openDialogComponent(
                WarningDialogComponent,
                { self: this, type, showTheStarter: planId === "Free" },
                "800px"
              );
              return EMPTY;
            }
            if (errorResponse.error?.error == "You can not add more than 3 font per flycut with current plan") {
              type = "editorFonts";
              this.dialog.openDialogComponent(
                WarningDialogComponent,
                { self: this, type, showTheStarter: false },
                "800px"
              );
              return EMPTY;
            }
            if (info.url == "v1/user/shortcut-category") {
              type = "categories";
              let error = errorResponse.error.error.indexOf("sub categories") != -1 ? "subCategories" : "categories";
              error += planId === "Free" ? "Free" : "Starter";
              this.dialog.openDialogComponent(
                WarningDialogComponent,
                { self: this, type, showTheStarter: planId === "Free", error },
                "800px"
              );
              return EMPTY;
            }
            if (info.url == "v1/user/shortcut_version/rollback") {
              type = planId === "Free" ? "versionHistory" : "versionHistoryForStarter";
              this.dialog.openDialogComponent(
                WarningDialogComponent,
                { self: this, type, showTheStarter: planId === "Free" },
                "800px"
              );
              return EMPTY;
            }
          }
          if (errorResponse.error.error_list) {
            this.handleValidationErrorList(errorResponse.error.error_list);
          } else if (errorResponse.error.error) {
            this.notification.toast(errorResponse.error.message || errorResponse.error.error, "OK", 6000);
          }
          if (
            errorResponse.status == 400 &&
            errorResponse.error.result &&
            errorResponse.error.result.Response &&
            errorResponse.error.result.Response[0] &&
            errorResponse.error.result.Response[0]["@attributes"]["Result"] == "Error"
          ) {
            this.notification.toast(
              errorResponse.error.result.Response[0]["@attributes"]["ResultDescription"] ??
                "Error with authenticating you on FlyLearning",
              "OK",
              6000
            );
          }

          if (errorResponse.status == 400 && errorResponse.error.result && errorResponse.error.result.message) {
            this.notification.toast(errorResponse.error.result.message, "OK", 6000);
          }
        }
      }
    }

    return throwError(errorResponse);
  }

  toSave(data: {}, fields: string[]) {
    const obj = {};
    forEach(fields, (field: string) => {
      obj[field] = data[field];
    });
    return cloneDeep(obj);
  }

  searchTrack({ q }: SearchModel): Observable<any> {
    return this.http.get(`${this.apiURL}v1/user/search-data?query=${q}`);
  }

  flycutExistsSearch({ q }: SearchModel): Observable<any> {
    return this.http.get(`${this.apiURL}v1/user/flycuts-search-name?query=${q}`);
  }

  getCategories(): Observable<any> {
    return this.http.get(`${this.apiURL}v1/user/categories-search-data`);
  }

  getAdvancedSearchResult(filters: Record<string, any>): Observable<any> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      // Skip non-primitive values so a stray object never becomes "[object Object]"
      if (typeof value === 'object') {
        continue;
      }
      params = params.set(key, String(value));
    }
    // The success path stays a bare http.get (this endpoint's payload is not
    // the standard envelope), but failures go through handleError like every
    // other call — otherwise the backend's plan block (422, see handleError)
    // is swallowed and the user just gets an empty page.
    // `isLoader: false`: the caller owns its own CONTENT_VIEW_LOADER.
    return this.http
      .get(`${this.apiURL}${ADVANCED_SEARCH_URL}`, { params })
      .pipe(catchError((err: Response) => this.handleError(err, { url: ADVANCED_SEARCH_URL, isLoader: false })));
  }

  trackVersionUpdate(front_version) {
    if (!front_version) {
      return;
    }
    let local_front_version = this.sharedData.getAttribute("FlyMSGـFrontEndـVersion");
    let refresh = false;

    if (!local_front_version) {
      this.sharedData.setAttribute("FlyMSGـFrontEndـVersion", front_version);
    }

    if (local_front_version && front_version != local_front_version) {
      this.sharedData.setAttribute("FlyMSGـFrontEndـVersion", front_version);
      refresh = true;
    }

    //refresh
    if (refresh) {
      location.reload();
    }
  }

  updatePlanStore(requestType: string | null, url: string): void {
    const update =
      (["post", "put", "delete"].includes(requestType) &&
        (url.includes("/user/shortcut-category") ||
          url.includes("/user/copy-shortcut/template/single") ||
          url.includes("/user/copy-shortcut/category/single"))) ||
      (["post", "delete"].includes(requestType) && url.includes("/user/shortcut"));

    if (update) {
      this.store.dispatch(loadUserCurrentSubscriptionApi({ refetchFromApi: true }));
    }
  }

  private get apiURL(): string {
    return environment.API_URL;
  }
}

export class SearchModel {
  q: string;
}
